import React, { useCallback, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { SEARCE_COLORS } from '../../theme';

const RADIAN = Math.PI / 180;

function numericOuterRadius(outerRadius: unknown, fallback: number): number {
  if (typeof outerRadius === 'number' && !Number.isNaN(outerRadius)) return outerRadius;
  return fallback;
}

const LABEL_PAD = 6;
const LABEL_BOX_MAX_W = 118;
/** foreignObject height — compact “tooltip” card */
const LABEL_FO_HEIGHT = 58;
/** Radial offset from Recharts label anchor (smaller = labels stay closer to ring, inside plot) */
const CALLOUT_OFFSET_RADIUS = 40;
/** Push label box slightly along slice normal (keep modest so labels stay inside margins) */
const RADIAL_LABEL_BOX_SHIFT = 8;
/** Vertical / horizontal stagger between callouts on the same side (px) */
const STAGGER_STEP = 22;

type StaggerKey = 'right' | 'left' | 'top' | 'bottom';

function clampCalloutBoxX(labelX: number, chartW: number, boxW: number, textAnchor: string): number {
  const innerRight = chartW - LABEL_PAD;
  const innerLeft = LABEL_PAD;

  if (textAnchor === 'start') {
    let x = labelX;
    if (x + boxW > innerRight) x = innerRight - boxW;
    if (x < innerLeft) x = innerLeft;
    return x;
  }
  if (textAnchor === 'end') {
    let x = labelX - boxW;
    if (x < innerLeft) x = innerLeft;
    if (x + boxW > innerRight) x = innerRight - boxW;
    return x;
  }
  let x = labelX - boxW / 2;
  if (x < innerLeft) x = innerLeft;
  if (x + boxW > innerRight) x = innerRight - boxW;
  return x;
}

function clampY(y: number, chartH: number, boxH: number): number {
  const innerBottom = chartH - LABEL_PAD;
  const innerTop = LABEL_PAD;
  let yy = y;
  if (yy + boxH > innerBottom) yy = innerBottom - boxH;
  if (yy < innerTop) yy = innerTop;
  return yy;
}

function rectsOverlap(
  a: { left: number; top: number; right: number; bottom: number },
  b: { left: number; top: number; right: number; bottom: number },
  pad: number
): boolean {
  return !(
    a.right + pad <= b.left ||
    b.right + pad <= a.left ||
    a.bottom + pad <= b.top ||
    b.bottom + pad <= a.top
  );
}

function pickStaggerKey(midAngleDeg: number): StaggerKey {
  const a = -RADIAN * midAngleDeg;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  if (Math.abs(cos) >= Math.abs(sin)) {
    return cos >= 0 ? 'right' : 'left';
  }
  return sin >= 0 ? 'bottom' : 'top';
}

export interface DonutSegmentData {
  name: string;
  value: number;
  color: string;
}

/** Project Metrics / Funding / DRI tier donuts — compact ring (~28% of radius), large center; details on hover. */
export const INSIGHTS_DONUT_DIMENSIONS = {
  height: 320,
  innerRadius: 72,
  outerRadius: 100,
} as const;

type PieCalloutPayload = DonutSegmentData & { layoutWeight: number };

export interface SegmentDonutChartProps {
  data: DonutSegmentData[];
  centerValue?: number;
  centerLabel?: string;
  height?: number;
  innerRadius?: number | string;
  outerRadius?: number | string;
  sliceVisualExponent?: number;
  tooltipUnitLabel?: string;
  hideTooltip?: boolean;
  /** When true (default): color swatch + name row under the donut. Set false for chart-only layout. */
  showBottomLegend?: boolean;
  /**
   * When true: leader-line callouts on the chart. When false (default): clean donut; segment name/value/% in hover tooltip.
   */
  showFixedTooltip?: boolean;
}

/**
 * Donut + center total. Default: bottom color legend + hover tooltip for values; no perimeter callout labels.
 * Opt in to {@link SegmentDonutChartProps.showFixedTooltip} for permanent callouts.
 */
const SegmentDonutChart: React.FC<SegmentDonutChartProps> = ({
  data,
  centerValue: centerValueProp,
  centerLabel = 'Total',
  height = 380,
  innerRadius = 52,
  outerRadius = 118,
  sliceVisualExponent = 0.65,
  tooltipUnitLabel = 'solvers',
  hideTooltip = false,
  showBottomLegend = true,
  showFixedTooltip = false,
}) => {
  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);
  const centerDisplay = centerValueProp !== undefined ? centerValueProp : total;
  const showHoverTooltip = !hideTooltip && !showFixedTooltip;

  /** Reset each render so stagger counts match slice draw order */
  const staggerRef = useRef<Record<StaggerKey, number>>({
    right: 0,
    left: 0,
    top: 0,
    bottom: 0,
  });
  const placedLabelRectsRef = useRef<{ left: number; top: number; right: number; bottom: number }[]>([]);
  if (showFixedTooltip) {
    staggerRef.current = { right: 0, left: 0, top: 0, bottom: 0 };
    placedLabelRectsRef.current = [];
  }

  const pieOuterRadius = useMemo(() => {
    if (!showFixedTooltip) return outerRadius;
    if (typeof outerRadius === 'number' && !Number.isNaN(outerRadius)) {
      return Math.max(outerRadius * 0.86, outerRadius - 14);
    }
    return outerRadius;
  }, [outerRadius, showFixedTooltip]);

  const renderOuterCalloutLabel = useCallback(
    (props: Record<string, unknown>) => {
      const cx = Number(props.cx);
      const cy = Number(props.cy);
      const midAngle = Number(props.midAngle);
      const payload = props.payload as PieCalloutPayload | undefined;
      const labelX = Number(props.x);
      const labelY = Number(props.y);
      const textAnchor = String(props.textAnchor || 'middle') as 'start' | 'end' | 'middle';
      const chartW = Number(props.width);
      const chartH = Number(props.height);
      const svgInnerW = Number.isFinite(chartW) && chartW > 0 ? chartW : 320;
      const svgInnerH = Number.isFinite(chartH) && chartH > 0 ? chartH : 320;

      if (!payload || payload.value <= 0 || !Number.isFinite(cx) || !Number.isFinite(cy)) return null;
      if (!Number.isFinite(labelX) || !Number.isFinite(labelY)) return null;

      const pctStr = total > 0 ? ((payload.value / total) * 100).toFixed(1) : '0.0';
      const or = numericOuterRadius(props.outerRadius, 100);
      const sin = Math.sin(-RADIAN * midAngle);
      const cos = Math.cos(-RADIAN * midAngle);

      const sx = cx + (or + 2) * cos;
      const sy = cy + (or + 2) * sin;
      const elbowR = or + 14;
      const mx = cx + elbowR * cos;
      const my = cy + elbowR * sin;

      const innerSpan = Math.max(40, svgInnerW - 2 * LABEL_PAD);
      const boxW = Math.min(LABEL_BOX_MAX_W, innerSpan);
      const boxX = clampCalloutBoxX(labelX, svgInnerW, boxW, textAnchor);

      const sk = pickStaggerKey(midAngle);
      const slot = staggerRef.current[sk]++;
      const staggerPx = slot * STAGGER_STEP;

      let foX = boxX + cos * RADIAL_LABEL_BOX_SHIFT;
      let foY = labelY - LABEL_FO_HEIGHT / 2 + sin * RADIAL_LABEL_BOX_SHIFT;
      if (sk === 'right' || sk === 'left') {
        foY += staggerPx;
      } else {
        foX += staggerPx * 0.55;
      }

      if (foX + boxW > svgInnerW - LABEL_PAD) foX = svgInnerW - LABEL_PAD - boxW;
      if (foX < LABEL_PAD) foX = LABEL_PAD;
      foY = clampY(foY, svgInnerH, LABEL_FO_HEIGHT);

      const makeRect = (x: number, y: number) => ({
        left: x,
        top: y,
        right: x + boxW,
        bottom: y + LABEL_FO_HEIGHT,
      });
      const OVERLAP_PAD = 4;
      let foXn = foX;
      let foYn = foY;
      for (let tries = 0; tries < 48; tries++) {
        const candidate = makeRect(foXn, foYn);
        const hit = placedLabelRectsRef.current.some((p) => rectsOverlap(candidate, p, OVERLAP_PAD));
        if (!hit) break;
        if (tries < 30) {
          foYn += 12;
        } else if (sk === 'right') {
          foXn -= 12;
        } else if (sk === 'left') {
          foXn += 12;
        } else {
          foXn += tries % 2 === 0 ? 14 : -14;
        }
        foXn = Math.min(Math.max(foXn, LABEL_PAD), svgInnerW - LABEL_PAD - boxW);
        foYn = clampY(foYn, svgInnerH, LABEL_FO_HEIGHT);
      }
      placedLabelRectsRef.current.push(makeRect(foXn, foYn));
      foX = foXn;
      foY = foYn;

      const joinY = foY + LABEL_FO_HEIGHT / 2;
      const lineEndX =
        textAnchor === 'start' ? foX : textAnchor === 'end' ? foX + boxW : foX + boxW / 2;

      const textAlign: 'left' | 'right' | 'center' =
        textAnchor === 'start' ? 'left' : textAnchor === 'end' ? 'right' : 'center';

      return (
        <g>
          <path
            d={`M${sx},${sy}L${mx},${my}L${lineEndX},${joinY}`}
            stroke={SEARCE_COLORS.neutral_400}
            strokeWidth={1}
            fill="none"
          />
          <foreignObject x={foX} y={foY} width={boxW} height={LABEL_FO_HEIGHT}>
            <div
              {...({ xmlns: 'http://www.w3.org/1999/xhtml' } as Record<string, string>)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                boxSizing: 'border-box',
                padding: '6px 8px',
                backgroundColor: SEARCE_COLORS.white,
                border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                borderRadius: 6,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
                textAlign,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: SEARCE_COLORS.textPrimary,
                  lineHeight: 1.2,
                  marginBottom: 2,
                }}
              >
                {payload.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: payload.color,
                  lineHeight: 1.2,
                }}
              >
                {payload.value.toLocaleString()} ({pctStr}%)
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 400,
                  color: SEARCE_COLORS.textSecondary,
                  marginTop: 2,
                }}
              >
                {tooltipUnitLabel}
              </div>
            </div>
          </foreignObject>
        </g>
      );
    },
    [total, tooltipUnitLabel]
  );

  const pieLabelWithRadialOffset = useMemo(() => {
    const fn = Object.assign(
      (p: Record<string, unknown>) => renderOuterCalloutLabel(p),
      { offsetRadius: CALLOUT_OFFSET_RADIUS }
    );
    return fn;
  }, [renderOuterCalloutLabel]);

  const pieData = useMemo(() => {
    return data
      .filter((d) => d.value > 0)
      .map((d) => ({
        ...d,
        layoutWeight: Math.pow(d.value, sliceVisualExponent),
      }));
  }, [data, sliceVisualExponent]);

  if (pieData.length === 0) {
    return (
      <Box sx={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data to display
        </Typography>
      </Box>
    );
  }

  const pieMargin = showFixedTooltip
    ? { top: 34, right: 34, bottom: 34, left: 34 }
    : { top: 8, right: 8, bottom: 8, left: 8 };

  const sectorPaddingAngle = showFixedTooltip ? (pieData.length > 1 ? 2 : 0) : pieData.length > 1 ? 2.5 : 0;
  const sectorCornerRadius = showFixedTooltip ? 2 : 6;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: '100%',
        px: { xs: 0.5, sm: 1 },
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
        outline: 'none',
        '&:focus': { outline: 'none' },
        '&:focus-visible': { outline: 'none' },
        '& .recharts-wrapper': {
          overflow: 'hidden',
          maxWidth: '100%',
          outline: 'none',
          '&:focus': { outline: 'none' },
        },
        '& .recharts-surface': {
          overflow: 'hidden',
          maxWidth: '100%',
          outline: 'none',
          '&:focus': { outline: 'none' },
        },
        '& svg': {
          outline: 'none',
          maxWidth: '100%',
          '&:focus': { outline: 'none' },
        },
        '& .recharts-layer': { outline: 'none' },
        '& path:focus': { outline: 'none' },
        '& .recharts-wrapper *:focus': { outline: 'none' },
        '& .recharts-wrapper *:focus-visible': { outline: 'none' },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flex: '1 1 0',
          minHeight: 160,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={pieMargin}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              paddingAngle={sectorPaddingAngle}
              cornerRadius={sectorCornerRadius}
              label={showFixedTooltip ? pieLabelWithRadialOffset : false}
              labelLine={false}
              outerRadius={pieOuterRadius}
              innerRadius={innerRadius}
              dataKey="layoutWeight"
              cursor={showHoverTooltip ? 'pointer' : undefined}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke={SEARCE_COLORS.cardBackground} strokeWidth={1} />
              ))}
            </Pie>
            {showHoverTooltip && (
              <RechartsTooltip
                content={(props: any) => {
                  const { active, payload } = props;
                  if (!active || !payload?.length) return null;
                  const row = payload[0].payload as DonutSegmentData;
                  return (
                    <Box
                      sx={{
                        backgroundColor: SEARCE_COLORS.white,
                        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        pointerEvents: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: row.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
                          {row.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: row.color, fontWeight: 700, fontSize: '0.8125rem' }}>
                        {row.value.toLocaleString()}
                        {total > 0 ? ` (${((row.value / total) * 100).toFixed(1)}%)` : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, display: 'block', mt: 0.25 }}>
                        {tooltipUnitLabel}
                      </Typography>
                    </Box>
                  );
                }}
                allowEscapeViewBox={{ x: true, y: true }}
                wrapperStyle={{ zIndex: 10, pointerEvents: 'none' }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 2,
            maxWidth: showFixedTooltip ? '48%' : '56%',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: SEARCE_COLORS.textPrimary,
              fontSize: showFixedTooltip ? '24px' : '26px',
              lineHeight: 1.2,
            }}
          >
            {typeof centerDisplay === 'number' ? centerDisplay.toLocaleString() : centerDisplay}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: SEARCE_COLORS.textSecondary,
              fontSize: '11px',
              mt: 0.5,
            }}
          >
            {centerLabel}
          </Typography>
        </Box>
      </Box>

      {showBottomLegend && (
        <Box
          component="ul"
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center',
            columnGap: 2,
            rowGap: 0.75,
            mt: 'auto',
            pt: 1,
            pb: 0.5,
            px: 1,
            m: 0,
            listStyle: 'none',
            width: '100%',
          }}
        >
          {pieData.map((entry) => (
            <Box
              key={entry.name}
              component="li"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: SEARCE_COLORS.textPrimary,
                  lineHeight: 1.2,
                }}
              >
                {entry.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SegmentDonutChart;
